import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UploadCloud, CheckCircle, XCircle, AlertTriangle, FileSpreadsheet } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import { fmtNumber, fmtDate } from '../components/Staffing/format';
import { c, t, sp, card, btn, tbl } from '../theme';

export default function StaffingTrackerUpload() {
  const qc = useQueryClient();
  const [file,     setFile]     = useState(null);
  const [progress, setProgress] = useState(0);
  const [preview,  setPreview]  = useState(null);   // { batch_id, preview: {...} }
  const inputRef = useRef(null);

  const { data: batches = [], isLoading: batchesLoading } = useQuery({
    queryKey: ['staffing-batches'],
    queryFn:  () => api.get('/staffing/batches'),
    refetchInterval: 15000,
  });

  // ── Step 1: upload + parse (preview only) ──────────────────
  const uploadMutation = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append('file', file);
      return api.upload('/staffing/upload', fd, setProgress);
    },
    onSuccess: (data) => { setPreview(data); setProgress(0); },
    onError:   (err) => { toast.error(err.message || 'Upload failed.'); setProgress(0); },
  });

  // ── Step 2a: confirm ───────────────────────────────────────
  const confirmMutation = useMutation({
    mutationFn: () => api.commit('/staffing/upload/confirm', { batch_id: preview.batch_id }),
    onSuccess: (data) => {
      toast.success(`Committed ${fmtNumber(data.rosterRowCount)} roster + ${fmtNumber(data.vacancyRowCount)} vacancy rows (${fmtNumber(data.unmappedRowCount)} unmapped).`);
      resetAll();
      qc.invalidateQueries({ queryKey: ['staffing-batches'] });
      qc.invalidateQueries({ queryKey: ['staffing-summary'] });
      qc.invalidateQueries({ queryKey: ['staffing-vacancies'] });
    },
    onError: (err) => toast.error(err.message || 'Confirm failed.'),
  });

  // ── Step 2b: cancel ────────────────────────────────────────
  const cancelMutation = useMutation({
    mutationFn: () => api.post('/staffing/upload/cancel', { batch_id: preview.batch_id }),
    onSuccess: () => { toast('Upload discarded.'); resetAll(); qc.invalidateQueries({ queryKey: ['staffing-batches'] }); },
    onError:   (err) => toast.error(err.message || 'Cancel failed.'),
  });

  function resetAll() {
    setFile(null); setPreview(null); setProgress(0);
    if (inputRef.current) inputRef.current.value = '';
  }

  const p = preview?.preview;
  const busy = uploadMutation.isPending || confirmMutation.isPending || cancelMutation.isPending;

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Administration"
        title="Staffing Tracker Upload"
        subtitle="Import the Athens Staffing & Development Tracker (the Workday-sourced Roster and Vacancies tabs — every other tab in that workbook is a computed view and is ignored here). The file is parsed and previewed first — nothing is saved until you confirm. Committing replaces the current staffing snapshot."
      />

      {/* ── Step 1: drop zone (hidden once we have a preview) ── */}
      {!preview && (
        <div style={s.card}>
          <div
            style={{ ...s.dropZone, borderColor: file ? '#86EFAC' : c.borderStrong }}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setFile(f); }}
          >
            <input ref={inputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                   onChange={(e) => setFile(e.target.files[0] || null)} />
            <UploadCloud size={26} color={file ? c.success : c.textFaint} />
            <div style={s.dropText}>
              {file ? (
                <><strong style={{ color: c.success }}>{file.name}</strong><br /><span style={{ color: c.textFaint }}>{(file.size / 1024).toFixed(0)} KB</span></>
              ) : (
                <><strong>Click to select</strong> or drag &amp; drop<br /><span style={{ color: c.textFaint }}>Staffing Tracker .xlsx or .xls</span></>
              )}
            </div>
          </div>

          {uploadMutation.isPending && (
            <div style={s.progressWrap}>
              <div style={s.progressTrack}><div style={{ ...s.progressFill, width: `${progress}%` }} /></div>
              <span style={s.progressLabel}>{progress}%</span>
            </div>
          )}

          <button
            style={{ ...btn.primary, opacity: file && !busy ? 1 : 0.5 }}
            disabled={!file || busy}
            onClick={() => uploadMutation.mutate()}
          >
            {uploadMutation.isPending ? 'Parsing…' : 'Parse & Preview'}
          </button>
        </div>
      )}

      {/* ── Step 2: preview + confirm/cancel ─────────────────── */}
      {p && (
        <div style={s.card}>
          <div style={s.previewHead}>
            <FileSpreadsheet size={18} color={c.navy} />
            <span style={s.previewTitle}>Preview — {preview.file_name || file?.name || 'staffing tracker file'}</span>
          </div>

          <div style={s.statGrid}>
            <Stat label="Roster Rows" value={fmtNumber(p.roster_row_count)} />
            <Stat label="Vacancy Rows" value={fmtNumber(p.vacancy_row_count)} />
            <Stat label="Unmapped Rows" value={fmtNumber(p.unmapped_row_count)} accent={p.unmapped_row_count > 0 ? c.warningText : undefined} />
            <Stat label="Frozen/Evergreen Excluded" value={fmtNumber(p.excluded_frozen_evergreen_count)} />
          </div>

          {/* Vacancies by category */}
          {p.vacancies_by_category?.length > 0 && (
            <div style={s.subBlock}>
              <div style={s.subLabel}>Vacancies by Category</div>
              <div style={s.chips}>
                {p.vacancies_by_category.map((cat) => (
                  <span key={cat.category} style={s.chip}>{cat.category} · {fmtNumber(cat.count)}</span>
                ))}
              </div>
            </div>
          )}

          {/* Unmapped cost centers */}
          {p.unmapped_row_count > 0 && (
            <div style={s.warnBlock}>
              <div style={s.warnHead}><AlertTriangle size={14} /> {fmtNumber(p.unmapped_row_count)} row(s) at cost centers that didn't match a known facility code</div>
              <ul style={s.warnList}>
                {p.unmapped_cost_centers.slice(0, 15).map((n) => <li key={n} style={s.warnItem}>{n}</li>)}
                {p.unmapped_cost_centers.length > 15 && <li style={s.warnItem}>…and {p.unmapped_cost_centers.length - 15} more.</li>}
              </ul>
              <div style={s.warnFoot}>These rows still commit, but are excluded from cost-center totals until the raw value above is corrected in the source file (or the facility code list is extended).</div>
            </div>
          )}

          {/* Parse warnings */}
          {p.warning_count > 0 && (
            <div style={s.warnBlock}>
              <div style={s.warnHead}><AlertTriangle size={14} /> {fmtNumber(p.warning_count)} row warning(s)</div>
              <ul style={s.warnList}>
                {p.warnings.slice(0, 8).map((w, i) => <li key={i} style={s.warnItem}>{w}</li>)}
                {p.warning_count > 8 && <li style={s.warnItem}>…and {fmtNumber(p.warning_count - 8)} more.</li>}
              </ul>
            </div>
          )}

          {/* Sample rows */}
          {p.roster_sample?.length > 0 && (
            <div style={s.subBlock}>
              <div style={s.subLabel}>Roster Sample</div>
              <div style={s.sampleWrap}>
                <div style={{ overflowX: 'auto' }}>
                  {/* Wide table: it scrolls inside this, so the page does not. */}
                <table style={s.sampleTable}>
                  <thead>
                    <tr>{['Worker', 'Job Profile', 'Cost Center'].map((h) => <th key={h} style={tbl.thSm}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {p.roster_sample.map((r, i) => (
                      <tr key={i} style={tbl.tr}>
                        <td style={tbl.tdSm}>{r.worker_name}</td>
                        <td style={tbl.tdSm}>{r.job_profile || '—'}</td>
                        <td style={tbl.tdSm}>{r.raw_cost_center || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          )}
          {p.vacancy_sample?.length > 0 && (
            <div style={s.subBlock}>
              <div style={s.subLabel}>Vacancies Sample</div>
              <div style={s.sampleWrap}>
                <div style={{ overflowX: 'auto' }}>
                  {/* Wide table: it scrolls inside this, so the page does not. */}
                <table style={s.sampleTable}>
                  <thead>
                    <tr>{['Req #', 'Title', 'Category', 'Cost Center', 'Days Open'].map((h, i) => <th key={h} style={i === 4 ? tbl.thRSm : tbl.thSm}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {p.vacancy_sample.map((v, i) => (
                      <tr key={i} style={tbl.tr}>
                        <td style={tbl.tdSm}>{v.requisition_id || '—'}</td>
                        <td style={tbl.tdSm}>{v.requisition_title || '—'}</td>
                        <td style={tbl.tdSm}>{v.recruiting_category || '—'}</td>
                        <td style={tbl.tdSm}>{v.raw_cost_center || '—'}</td>
                        <td style={tbl.tdRSm}>{fmtNumber(v.days_open)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          )}

          <div style={s.actionRow}>
            <button style={{ ...btn.primary, opacity: busy ? 0.5 : 1 }} disabled={busy}
                    onClick={() => confirmMutation.mutate()}>
              <CheckCircle size={15} /> {confirmMutation.isPending ? 'Committing…' : 'Confirm & Commit'}
            </button>
            <button style={{ ...btn.secondary, opacity: busy ? 0.5 : 1 }} disabled={busy}
                    onClick={() => cancelMutation.mutate()}>
              <XCircle size={15} /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Ingestion history ────────────────────────────────── */}
      <div style={s.logSection}>
        <div style={s.logTitle}>Recent Imports</div>
        {batchesLoading ? <LoadingSpinner /> : (
          <div style={{ overflowX: 'auto' }}>
            {/* Wide table: it scrolls inside this, so the page does not. */}
          <table style={s.logTable}>
            <thead>
              <tr>{['File', 'Source', 'Status', 'Current', 'Roster', 'Vacancies', 'Unmapped CC', 'Unmapped Category', 'Frozen/Evergreen', 'By', 'When'].map((h) => <th key={h} style={tbl.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {batches.map((b) => {
                const st = STATUS_STYLE[b.status] || STATUS_STYLE.preview;
                return (
                  <tr key={b.id} style={tbl.tr}>
                    <td style={tbl.td} title={b.file_name}>{b.file_name?.slice(-40)}</td>
                    <td style={tbl.td}>{b.source === 'ftp' ? '⟳ FTP' : '↑ Manual'}</td>
                    <td style={tbl.td}><span style={{ ...s.statusPill, color: st.color, background: st.bg }}>{st.icon} {b.status}</span></td>
                    <td style={tbl.td}>{b.is_current ? '✓' : ''}</td>
                    <td style={tbl.tdR}>{fmtNumber(b.roster_row_count)}</td>
                    <td style={tbl.tdR}>{fmtNumber(b.vacancy_row_count)}</td>
                    <td style={tbl.tdR}>{fmtNumber(b.unmapped_row_count)}</td>
                    <td style={{ ...tbl.tdR, color: b.unmapped_category_row_count > 0 ? c.warningText : undefined }}>
                      {fmtNumber(b.unmapped_category_row_count)}
                    </td>
                    <td style={tbl.tdR}>{fmtNumber(b.excluded_frozen_evergreen_count)}</td>
                    <td style={tbl.td}>{b.uploaded_by_name || (b.source === 'ftp' ? 'System' : '—')}</td>
                    <td style={tbl.td}>{b.created_at ? new Date(b.created_at).toLocaleString() : '—'}</td>
                  </tr>
                );
              })}
              {!batches.length && <tr><td colSpan={11} style={s.emptyLog}>No imports yet.</td></tr>}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div style={s.stat}>
      <div style={{ ...s.statVal, ...(accent ? { color: accent } : {}) }}>{value}</div>
      <div style={s.statLabel}>{label}</div>
    </div>
  );
}

const STATUS_STYLE = {
  committed: { color: c.successText, bg: c.successTint, icon: <CheckCircle size={12} /> },
  preview:   { color: c.info,        bg: c.infoTint,    icon: <AlertTriangle size={12} /> },
  cancelled: { color: c.textFaint,   bg: c.canvas,      icon: <XCircle size={12} /> },
  failed:    { color: c.dangerText,  bg: c.dangerTint,  icon: <XCircle size={12} /> },
};

const s = {
  page:        { maxWidth: '1000px', margin: '0 auto' },
  card:        { ...card.sm, padding: '16px', marginBottom: sp[5] },
  dropZone:    { borderWidth: '2px', borderStyle: 'dashed', borderRadius: '10px', padding: '30px', textAlign: 'center', cursor: 'pointer', marginBottom: sp[4], display: 'flex', flexDirection: 'column', alignItems: 'center', gap: sp[2] },
  dropText:    { fontSize: t.bodySm.fontSize, color: c.textStrong, lineHeight: 1.6 },
  progressWrap:{ display: 'flex', alignItems: 'center', gap: sp[2], marginBottom: sp[4] },
  progressTrack:{ flex: 1, height: '5px', background: c.border, borderRadius: '4px', overflow: 'hidden' },
  progressFill:{ height: '100%', background: c.navy, borderRadius: '4px', transition: 'width 0.2s' },
  progressLabel:{ fontSize: t.caption.fontSize, fontWeight: 600, color: c.textStrong, minWidth: '30px' },
  previewHead: { display: 'flex', alignItems: 'center', gap: sp[2], marginBottom: sp[4] },
  previewTitle:{ fontSize: t.h3.fontSize, fontWeight: 700, color: c.text },
  statGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: sp[3], marginBottom: sp[5] },
  stat:        { ...card.sunk, padding: '11px 13px' },
  statVal:     { fontSize: t.h2.fontSize, fontWeight: 700, color: c.text },
  statLabel:   { ...t.label, color: c.textMuted, marginTop: '3px' },
  subBlock:    { marginBottom: sp[4] },
  subLabel:    { ...t.label, color: c.textMuted, marginBottom: sp[2] },
  chips:       { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  chip:        { fontSize: t.caption.fontSize, color: c.textStrong, background: c.canvas, borderRadius: '6px', padding: '4px 10px' },
  warnBlock:   { background: c.warningTint, border: '1px solid #FDE68A', borderRadius: '10px', padding: '12px 14px', marginBottom: sp[4] },
  warnHead:    { display: 'flex', alignItems: 'center', gap: '6px', fontSize: t.caption.fontSize, fontWeight: 700, color: c.warningText, marginBottom: '6px' },
  warnList:    { margin: 0, paddingLeft: '18px' },
  warnItem:    { fontSize: t.caption.fontSize, color: c.warningText, lineHeight: 1.6 },
  warnFoot:    { fontSize: t.micro.fontSize, color: c.warningText, marginTop: '6px' },
  sampleWrap:  { border: `1px solid ${c.border}`, borderRadius: '8px', overflow: 'auto' },
  sampleTable: { width: '100%', borderCollapse: 'collapse', fontSize: t.caption.fontSize },
  actionRow:   { display: 'flex', gap: sp[2], marginTop: '6px' },
  logSection:  { ...card.sm, overflow: 'hidden' },
  logTitle:    { ...card.head },
  logTable:    { width: '100%', borderCollapse: 'collapse', fontSize: t.bodySm.fontSize },
  statusPill:  { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', fontSize: t.caption.fontSize, fontWeight: 500 },
  emptyLog:    { textAlign: 'center', padding: '24px', color: c.textFaint },
};
